import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useUsersActions } from "tap/store/users/useUsers.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates user data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchUsers function and user state values
 */
export function useUsersData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateUsers } = useUsersActions();
  const { filterForm, userRange, userSort, itemsPerPage } = useSelector(
    (s) => s.users,
  );

  /**
   * Fetches users from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {string} range - The pagination range (e.g., "0,25")
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux filterForm
   * @param {Array|null} customSort - Optional sort array (e.g., ["field", "ASC"]) to use instead of Redux userSort
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchUsers = useCallback(
    async (range, customFilterData = null, customSort = null) => {
      const startTime = Date.now();
      setLoadingTrue();

      const filterData = customFilterData ?? filterForm;
      const sortData = customSort ?? userSort;

      try {
        const data = await tapApi.users(
          filterCriteria(filterData, "", "users"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          showErrorToast(data?.message || "Failed to fetch users.");
          return null;
        }

        return data;
      } catch (error) {
        showErrorToast(error?.message || "Error fetching users.");
        return null;
      } finally {
        // Ensure minimum loading duration for consistent UX
        const elapsed = Date.now() - startTime;
        const remainingTime = MIN_LOADING_DURATION - elapsed;

        if (remainingTime > 0) {
          setTimeout(() => setLoadingFalse(), remainingTime);
        } else {
          setLoadingFalse();
        }
      }
    },
    [filterForm, userSort, setLoadingTrue, setLoadingFalse, showErrorToast],
  );

  return {
    fetchUsers,
    updateUsers,
    itemsPerPage,
    userRange,
    filterForm,
    userSort,
  };
}
